import { useSelector } from 'react-redux';
import { AxisContainer } from '../../plot/components/AxisContainer';
import { EditableStrip } from '../../plot/components/EditableStrip';
import { GainPickerContainer } from '../../plot/components/GainPickerContainer';
import { GridContainer } from '../../plot/components/GridContainer';
import { LeadPickerContainer } from '../../plot/components/LeadPickerContainer';
import { PlotContainer } from '../../plot/components/PlotContainer';
import { StripContent } from '../../plot/components/StripContent';
import {
  StripEditor,
  STRIP_EDITOR_WIDTH,
} from '../../plot/components/StripEditor';
import { StripPosition } from '../../plot/components/StripPosition';
import { THREE_LEAD_DURATION } from '../../plot/components/ThreeLeadPlot';
import { Plot } from '../../plot/contexts/plot.context';
import { Row } from '../../plot/contexts/row.context';
import { Strip } from '../../plot/contexts/strip.context';
import {
  RAW_SIGNAL,
  squareToPixel,
  TOP_PLOT_HEADER_HEIGHT,
} from '../../plot/utils/graph.utils';
import { Ruler } from '../../ruler/components/Ruler';
import { RulerContainer } from '../../ruler/components/RulerContainer';
import { PlotHeader } from 'features/plot/components/PlotHeader';
import {
  RULER_HEADER_HEIGHT,
  RulerInfo,
} from '../../ruler/components/RulerInfo';
import { isToolActiveSelector } from '../../tool/selectors/tool.selectors';
import { Tool } from '../../tool/utils/tool.utils';
import {
  rawSignalLeadNamesSelector,
  rawSignalMeanSelector,
} from '../selectors/raw-signal.selectors';
import { RawLeads } from './RawLeads';

export const RAW_SIGNAL_STRIP_ID = `${RAW_SIGNAL}-strip`;

export const RawSignalPlot: React.FC = () => {
  const leadNames = useSelector(rawSignalLeadNamesSelector);
  const means = useSelector(rawSignalMeanSelector);
  const isRulerActive = useSelector(isToolActiveSelector(Tool.ruler));

  if (leadNames.length === 0) {
    return null;
  }

  const STRIP_HEIGHT = squareToPixel(4.1);
  const padding = squareToPixel(3.1);
  return (
    <PlotContainer>
      <Strip
        id={RAW_SIGNAL_STRIP_ID}
        duration={THREE_LEAD_DURATION}
        leads={[leadNames[0]]}
        mean={means[leadNames[0]]}
      >
        <Plot>
          <PlotHeader offset={STRIP_EDITOR_WIDTH} height={RULER_HEADER_HEIGHT}>
            <RulerInfo />
          </PlotHeader>
        </Plot>
        <Plot
          plotName='raw'
          data-testid='raw-signal-plot'
          cursor={isRulerActive ? 'crosshair' : undefined}
        >
          <Row
            height={STRIP_HEIGHT}
            padding={padding}
            topOffset={TOP_PLOT_HEADER_HEIGHT}
          >
            <StripEditor>
              <LeadPickerContainer leadCountThreshold={1} />
              <GainPickerContainer leadsShowing={true} />
            </StripEditor>
            <EditableStrip>
              <GridContainer />
              <StripPosition>
                <StripContent clipType='plot'>
                  <RawLeads />
                </StripContent>
                <RulerContainer>
                  <Ruler />
                </RulerContainer>
              </StripPosition>
            </EditableStrip>
          </Row>
        </Plot>
        <Plot>
          <AxisContainer offset={STRIP_EDITOR_WIDTH} />
        </Plot>
      </Strip>
    </PlotContainer>
  );
};
